import "./sidebar.css";
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { ChevronDown, Power, Settings } from "lucide-react";
import logo from "../../assets/logo_white.png";
import { menuItemAdm, menuItemUser, menuItemMaster } from "../../helpers/sections";
import { logoutUser } from "../../redux/apiCalls/loginCall";
import { userSuccess } from "../../redux/reducers/profileRedux";

const Sidebar = ({ isOpen, setIsOpen }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarRef = useRef();
  const [dropdown, setDropdown] = useState({});

  const toggleDropdown = (itemId, hasSubmenu) => {
    // Si el sidebar está colapsado y el item tiene submenú, expandir primero
    if (!isOpen && hasSubmenu) {
      setIsOpen(true);
    }
    // Luego toggle el dropdown
    setDropdown((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };
  const user = useSelector((state) => state.user);
  let menuItem;
  const privilege = user?.currentUser?.privilege;
  const accessTo = String(user?.currentUser?.access_to || "").toUpperCase();

  switch (privilege) {
    case "admin":
    case "admin-master":
      menuItem = menuItemAdm;
      break;
    case "master":
      menuItem = menuItemMaster;
      break;
    default:
      menuItem = menuItemUser;
  }

  const visibleMenuItem = menuItem.filter((item) => {
    if (!Array.isArray(item.allowedAccess) || item.allowedAccess.length === 0) {
      return true;
    }

    return item.allowedAccess.includes(accessTo);
  });

  const handleLogout = () => {
    try {
      sessionStorage.removeItem("registerDeviceDraft");
      sessionStorage.removeItem("registerDeviceReturn");
    } catch (e) {}
    dispatch(userSuccess(null));
    logoutUser(dispatch);
    navigate("/login");
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [setIsOpen]);

  return (
    <div className="cont-sidebar">
      {isOpen && <div className="overlay" onClick={() => setIsOpen(false)} />}
      <aside
        className={`sidebar ${
          isOpen ? "expanded" : window.innerWidth > 768 ? "collapsed" : "closed"
        }`}
        ref={sidebarRef}
      >
        {/* Logo Section */}
        <div className="sidebar-logo-section">
          <img
            src={logo}
            alt="SIGER"
            className={isOpen ? "logo-expanded" : "logo-collapsed"}
          />
        </div>

        {/* Menu List */}
        <nav className="sidebar-menu-list">
          {visibleMenuItem.map((item, index) => {
            const itemId = item.name.props?.id || `item-${index}`;
            const hasSubmenu = !item.path && item.subAdd;

            // Detectar si algún submenú está activo
            let submenuActive = false;
            if (hasSubmenu && item.subAdd) {
              submenuActive = item.subAdd.some(
                (e) => {
                  const submenuPath = `/${e.props.id}`;
                  return (
                    location.pathname === submenuPath ||
                    location.pathname.startsWith(`${submenuPath}/`)
                  );
                }
              );
            }

            // Renderizar ítem sin submenú
            if (!hasSubmenu) {
              return (
                <NavLink
                  key={index}
                  to={item.path}
                  className={({ isActive }) => {
                    let baseClass = "sidebar-link";
                    if (isActive) baseClass += " active";
                    if (!isOpen) baseClass += " collapsed-mode";
                    return baseClass;
                  }}
                >
                  <div className="sidebar-tooltip-wrapper">
                    <span className="sidebar-icon">{item.icon}</span>
                    {!isOpen && (
                      <span className="sidebar-tooltip">
                        {item.title}
                      </span>
                    )}
                  </div>
                  {isOpen && <span className="sidebar-text">{item.name}</span>}

                </NavLink>
              );
            }

            // Renderizar ítem con submenú
            const isDropdownOpen = (dropdown[itemId] || submenuActive) && isOpen;
            return (
              <div key={index} className="sidebar-menu-item">
                <button
                  className={`sidebar-menu-button ${
                    isDropdownOpen ? "open" : ""
                  } ${submenuActive ? "submenu-active" : ""} ${
                    !isOpen ? "collapsed-mode" : ""
                  }`}
                  onClick={() => toggleDropdown(itemId, hasSubmenu)}
                  title={!isOpen ? item.title : ""}
                  type="button"
                >
                  <div className="sidebar-tooltip-wrapper">
                    <span className="sidebar-icon">{item.icon}</span>

                    {!isOpen && (
                      <span className="sidebar-tooltip">
                        {item.title}
                      </span>
                    )}
                  </div>
                  {isOpen && (
                    <>
                      <span className="sidebar-text">{item.name}</span>
                      <ChevronDown className="chevron-icon" size={16} />
                    </>
                  )}
                </button>

                {isDropdownOpen && isOpen && (
                  <ul className="sidebar-submenu">
                    {item.subAdd.map((submenuItem, i) => (
                      <li key={i} className="submenu-item">
                        <NavLink
                          to={`/${submenuItem.props.id}`}
                          className={({ isActive }) =>
                            isActive
                              ? "submenu-link active"
                              : "submenu-link"
                          }
                        >
                          {submenuItem}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </nav>

        {/* Settings Section */}
        {isOpen && (
          <div className="sidebar-settings">
            {user.currentUser?.privilege !== "viewer" && (
              <NavLink
                to="/devices"
                className="settings-button"
                title="Configuración"
              >
                <Settings size={20} />
              </NavLink>
            )}
            <button
              className="settings-button logout-btn"
              onClick={handleLogout}
              title="Cerrar sesión"
              type="button"
            >
              <Power size={20} />
            </button>
          </div>
        )}
      </aside>
    </div>
  );
};

export default Sidebar;
